import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { ResetHistorySection } from '@/features/box-balance/BoxBalancePage';
import { useBoxResets } from '@/features/box-balance/useBoxBalance';

/** Where the archive lives — exported so the Reports list, App.tsx and the tests agree. */
export const BOX_RESETS_ARCHIVE_PATH = '/reports/archived/box-resets';

/**
 * Box resets, archived (owner, 2026-10-12: old box history stays readable).
 *
 * The cash drawer replaced the box balance, and the reset rows are the only
 * record of its counted daily handovers — a box reset wrote no audit row; the
 * `BoxReset` table is the trail. This page serves that table read-only from
 * `GET /box-balance/resets`, which stays readable after the switch-over,
 * through the very section the box screen used: null renders "not counted",
 * never zero, and the footer splits counted from uncounted resets. There is
 * no reset, no box and no edit here — nothing on this page moves money.
 */
export function BoxResetArchivePage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const navigate = useNavigate();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  // The key the history always needed, and the one the API asks for.
  const allowed = can('reports.view');
  const [page, setPage] = useState(1);
  const { data, error } = useBoxResets(allowed, page);

  if (!allowed) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('reports.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('reports.noViewPermission')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/reports')}
          title={t('reports.backToReports')}
          aria-label={t('reports.backToReports')}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-subtle text-content-secondary transition-colors hover:bg-hover hover:text-content"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </button>
        <h1 className="text-xl font-semibold text-content">{t('reports.boxResets.title')}</h1>
      </div>
      <p className="px-4 pt-2 text-xs text-content-muted">{t('reports.boxResets.note')}</p>

      <div className="min-h-0 flex-1 px-4 pb-6 pt-4">
        {data || error ? (
          <ResetHistorySection
            resets={data}
            error={error}
            currencySymbol={currencySymbol}
            onPage={setPage}
          />
        ) : (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        )}
      </div>
    </div>
  );
}
